import { intervalToSeconds, toChartTime } from "./indicators";
import { getMarketBySymbol, isCryptoMarket } from "./marketCatalog";

const DEFAULT_WS_BASE = "wss://stream.binance.com:9443/stream";

export const normalizeMarketInterval = (interval) => {
  if (interval.endsWith("s")) {
    return "1m";
  }

  return interval;
};

const createCryptoUrl = (symbol, interval) => {
  const normalizedSymbol = symbol.toLowerCase();
  const marketInterval = normalizeMarketInterval(interval);
  return `${DEFAULT_WS_BASE}?streams=${normalizedSymbol}@kline_${marketInterval}/${normalizedSymbol}@ticker`;
};

const createSeedCandle = (price, timestampSeconds) => ({
  time: timestampSeconds,
  open: price,
  high: price,
  low: price,
  close: price,
  volume: 100,
});

export const createMarketStream = ({
  symbol,
  interval = "1m",
  onKline,
  onTicker,
  onStatus,
  onError,
  onActivity,
}) => {
  const market = getMarketBySymbol(symbol);
  const cryptoMode = isCryptoMarket(symbol);
  let socket;
  let reconnectTimer;
  let heartbeatTimer;
  let simulatedTimer;
  let reconnectAttempts = 0;
  let closedManually = false;

  const emitStatus = (status) => {
    onStatus?.({
      status,
      reconnectAttempts,
      timestamp: Date.now(),
      transport: cryptoMode ? "websocket" : "simulated-stream",
    });
  };

  const emitActivity = (message, tone = "info") => {
    onActivity?.({
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      tone,
      message,
      source: cryptoMode ? "market-websocket" : "simulated-market-stream",
    });
  };

  const cleanup = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }

    if (simulatedTimer) {
      clearInterval(simulatedTimer);
      simulatedTimer = null;
    }
  };

  const startSimulatedStream = () => {
    emitStatus("connected");
    emitActivity(`Simulated ${market.label} stream online.`, "success");

    let lastPrice = market.seedPrice;
    const bucketSeconds = intervalToSeconds(interval);
    let currentBucket = Math.floor(Date.now() / 1000 / bucketSeconds) * bucketSeconds;
    let currentCandle = createSeedCandle(lastPrice, currentBucket);

    simulatedTimer = window.setInterval(() => {
      const drift = (Math.random() - 0.5) * market.volatility;
      lastPrice = Math.max(0.0001, lastPrice + drift);

      onTicker?.({
        symbol,
        lastPrice,
        priceChangePercent: ((lastPrice - market.seedPrice) / market.seedPrice) * 100,
        highPrice: Math.max(currentCandle.high, lastPrice),
        lowPrice: Math.min(currentCandle.low, lastPrice),
        volume: currentCandle.volume + Math.random() * 1200,
      });

      const nextBucket =
        Math.floor(Date.now() / 1000 / bucketSeconds) * bucketSeconds;

      if (nextBucket !== currentBucket) {
        onKline?.({
          symbol,
          openTime: currentBucket * 1000,
          closeTime: (currentBucket + bucketSeconds) * 1000,
          open: currentCandle.open,
          high: currentCandle.high,
          low: currentCandle.low,
          close: currentCandle.close,
          volume: currentCandle.volume,
          isClosed: true,
        });

        currentBucket = nextBucket;
        currentCandle = createSeedCandle(lastPrice, currentBucket);
        emitActivity(`New ${market.label} simulated candle opened.`, "info");
      } else {
        currentCandle = {
          ...currentCandle,
          close: lastPrice,
          high: Math.max(currentCandle.high, lastPrice),
          low: Math.min(currentCandle.low, lastPrice),
          volume: currentCandle.volume + Math.random() * 240,
        };
      }

      onKline?.({
        symbol,
        openTime: currentBucket * 1000,
        closeTime: (currentBucket + bucketSeconds) * 1000,
        open: currentCandle.open,
        high: currentCandle.high,
        low: currentCandle.low,
        close: currentCandle.close,
        volume: currentCandle.volume,
        isClosed: false,
      });
    }, 1500);
  };

  const scheduleReconnect = () => {
    if (closedManually || !cryptoMode) {
      return;
    }

    reconnectAttempts += 1;
    const delay = Math.min(2000 * reconnectAttempts, 15000);
    emitStatus("reconnecting");
    emitActivity(`Reconnecting to Binance stream in ${delay / 1000}s...`, "warn");
    reconnectTimer = window.setTimeout(connect, delay);
  };

  const connect = () => {
    cleanup();
    emitStatus("connecting");

    if (!cryptoMode) {
      startSimulatedStream();
      return;
    }

    socket = new WebSocket(createCryptoUrl(symbol, interval));

    socket.onopen = () => {
      reconnectAttempts = 0;
      emitStatus("connected");
      emitActivity(`Connected to Binance WebSocket stream for ${market.label}.`, "success");

      heartbeatTimer = window.setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({ method: "LIST_SUBSCRIPTIONS", id: Date.now() })
          );
        }
      }, 30000);
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const stream = payload.stream || "";
        const data = payload.data;

        if (stream.includes("@kline_") && data?.k) {
          onKline?.({
            symbol: data.s,
            openTime: data.k.t,
            closeTime: data.k.T,
            open: Number(data.k.o),
            high: Number(data.k.h),
            low: Number(data.k.l),
            close: Number(data.k.c),
            volume: Number(data.k.v),
            isClosed: data.k.x,
          });
        }

        if (stream.includes("@ticker") && data) {
          onTicker?.({
            symbol: data.s,
            lastPrice: Number(data.c),
            priceChangePercent: Number(data.P),
            highPrice: Number(data.h),
            lowPrice: Number(data.l),
            volume: Number(data.v),
          });
        }
      } catch (error) {
        onError?.(error);
      }
    };

    socket.onerror = (error) => {
      onError?.(error);
      emitStatus("error");
      emitActivity(`WebSocket error on ${market.label}.`, "error");
    };

    socket.onclose = () => {
      cleanup();
      emitStatus("disconnected");
      emitActivity(`Binance stream disconnected for ${market.label}.`, "warn");
      scheduleReconnect();
    };
  };

  connect();

  return {
    close() {
      closedManually = true;
      cleanup();

      if (socket && socket.readyState <= WebSocket.OPEN) {
        socket.close();
      }

      emitStatus("stopped");
      emitActivity(`Market stream stopped for ${market.label}.`, "warn");
    },
  };
};

export const toNormalizedCandle = (kline) => ({
  time: toChartTime(kline.openTime),
  open: kline.open,
  high: kline.high,
  low: kline.low,
  close: kline.close,
  volume: kline.volume,
});
